import { resolveGrowthEngineCtaPath } from './growthEngineCtaRoutes'
import {
  ollieCataloguePath,
  ollieFoodSafetyPath,
  ollieRecipesUsingProductPath,
} from './ollieActionDeepLinks'
import { buildInvoicesDeepLinkPath } from './reconciliationHealth'
import {
  GOODS_RECEIPTING_WIDEN_RANGE_DAYS,
  getWidenedGoodsReceiptingFilterDates,
} from './growthEngineGoodsReceiptingRangeEmpty'

/**
 * Phase 4 — what the Ollie panel shows on first open: the opening message and
 * up to 3 finding cards. Pure, so the panel only renders.
 *
 * Every figure is already in the finding copy from Express; nothing here
 * counts, prices or ages anything.
 */

const receiptingPo = params =>
  String(params?.purchaseOrderNumber || params?.q || '').trim()

const toIsoDay = value => {
  if (value == null || value === '') return null
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10)
  }
  const time = new Date(value).getTime()
  if (!Number.isFinite(time)) return null
  return new Date(time).toISOString().slice(0, 10)
}

/**
 * Ollie deep-links widen the GR window so the referenced PO is loadable.
 * Prefer orderedDate (±7 days, clamped into a 12-month lookback); else last 12 months.
 */
export function buildOllieReceiptingDateRangeParams(
  params = {},
  now = Date.now(),
) {
  const widened = getWidenedGoodsReceiptingFilterDates(now)
  const defaultStart = toIsoDay(widened.start)
  const defaultEnd = toIsoDay(widened.end)
  const orderedDay = toIsoDay(params.orderedDate || params.ordered_date)
  if (!orderedDay || !defaultStart || !defaultEnd) {
    return { start: defaultStart, end: defaultEnd }
  }

  const orderMs = new Date(`${orderedDay}T12:00:00.000Z`).getTime()
  const padMs = 7 * 24 * 60 * 60 * 1000
  let startMs = orderMs - padMs
  const earliestMs = Number(widened.start)
  if (Number.isFinite(earliestMs) && startMs < earliestMs) {
    startMs = earliestMs
  }
  // Never start after the default end (clock skew / bad orderedDate).
  if (startMs > Number(widened.end)) {
    startMs =
      Number(widened.end) -
      GOODS_RECEIPTING_WIDEN_RANGE_DAYS * 24 * 60 * 60 * 1000
  }

  return {
    start: toIsoDay(startMs) || defaultStart,
    end: defaultEnd,
  }
}

const buildReceiptingOlliePath = (venueId, filter, params = {}) => {
  const query = new URLSearchParams({ filter })
  const po = receiptingPo(params)
  if (po) query.set('q', po)
  const range = buildOllieReceiptingDateRangeParams(params)
  if (range.start) query.set('start', range.start)
  if (range.end) query.set('end', range.end)
  return `/v/${venueId}/purchasing/receipting?${query.toString()}`
}

/**
 * Express action `target` → dashboard screen. Targets not listed here (and
 * every prepare / draft action) open the finding's thread instead, where
 * Ollie builds the draft or change for the person to confirm. Builders get
 * the action's `params` (from the top evidence item) to land on the thing.
 */
const OLLIE_FINDING_TARGET_ROUTES = Object.freeze({
  goods_receipting_filtered: (venueId, params = {}) =>
    buildReceiptingOlliePath(venueId, 'needs_receipting', params),
  goods_receipting_needs_reconciling: (venueId, params = {}) =>
    buildReceiptingOlliePath(venueId, 'needs_reconciling', params),
  /** Outstanding Match-queue invoices; specific invoice when `invoiceId` is set. */
  invoices_outstanding: (venueId, params = {}) => {
    const invoiceId = String(params.invoiceId || '').trim()
    const po = receiptingPo(params)
    if (invoiceId) {
      return buildInvoicesDeepLinkPath(venueId, {
        review: true,
        invoiceId,
        openInWorkspace: true,
        q: po || undefined,
      })
    }
    return buildInvoicesDeepLinkPath(venueId, {
      review: true,
      q: po || undefined,
    })
  },
  invoices_upload: (venueId, params = {}) =>
    buildInvoicesDeepLinkPath(venueId, {
      upload: true,
      po: receiptingPo(params) || undefined,
      supplierId: String(params.supplierId || '').trim() || undefined,
      supplierTitle:
        String(params.supplier || params.supplierTitle || '').trim() ||
        undefined,
    }),
  /**
   * Discrepancy / credit ask: open the invoice in Match when known, else
   * Credit Requests filtered to the PO.
   */
  credit_requests: (venueId, params = {}) => {
    const invoiceId = String(params.invoiceId || '').trim()
    if (invoiceId) {
      return buildInvoicesDeepLinkPath(venueId, {
        invoiceId,
        openInWorkspace: true,
        q: receiptingPo(params) || undefined,
      })
    }
    const po = receiptingPo(params)
    const base = `/v/${venueId}/purchasing/creditnotes`
    if (!po) return base
    return `${base}?q=${encodeURIComponent(po)}`
  },
  product_price_compare: ollieCataloguePath,
  recipes_using_product: ollieRecipesUsingProductPath,
  recipes_needs_review: venueId => `/v/${venueId}/menu-builder`,
  recipes_missing_gp: venueId => `/v/${venueId}/menu-builder`,
  food_safety_log_reading: ollieFoodSafetyPath,
  food_safety_due_check: ollieFoodSafetyPath,
  stock_take_variance: venueId => `/v/${venueId}/stockTakes`,
  stock_take_open: (venueId, params = {}) =>
    params?.stockTakeId
      ? `/v/${venueId}/stockTakes/${encodeURIComponent(params.stockTakeId)}`
      : `/v/${venueId}/stockTakes`,
  spend_by_supplier: venueId => `/v/${venueId}/purchasing/spendTime`,
  supplier_order: venueId => `/v/${venueId}/products`,
  success_hub: venueId => `/v/${venueId}/progress`,
})

export const OLLIE_FINDING_CARD_COPY = Object.freeze({
  why: 'Why?',
  snooze: 'Remind me later',
  back: 'Back to what Ollie noticed',
  checking: 'Ollie is checking what needs you…',
  stale: 'A fresh check is running, so these may be slightly out of date.',
})

/** Mirrors Express `OLLIE_OPENING_COPY`, for recounting after Remind me later. */
export const OLLIE_OPENING_RECOUNT_COPY = Object.freeze({
  findingsOne: 'One thing needs you',
  findingsMany: '{n} things need you',
  topShown: 'Here are the top {n}.',
  allHidden: "That's everything for now",
})

const COUNT_WORDS = [
  '',
  'One',
  'Two',
  'Three',
  'Four',
  'Five',
  'Six',
  'Seven',
  'Eight',
  'Nine',
]

const fillCount = (template, n) =>
  template.replace('{n}', String(COUNT_WORDS[n] || n))

const greetingFrom = opening =>
  opening.greeting || String(opening.message || '').split('. ')[0] || ''

/**
 * Header for a findings opening once some cards are put away. Counts only
 * what still needs them; the "as of" is the one Express quoted.
 */
function recountOllieOpeningMessage(opening, { hiddenNeeds, shownNeeds }) {
  const total = Number.isInteger(opening.needsCount)
    ? opening.needsCount
    : shownNeeds + hiddenNeeds
  const remaining = Math.max(total - hiddenNeeds, 0)
  const greeting = greetingFrom(opening)
  const lead = greeting ? `${greeting}. ` : ''
  if (!remaining) {
    return `${lead}${OLLIE_OPENING_RECOUNT_COPY.allHidden}.`
  }
  const count =
    remaining === 1
      ? OLLIE_OPENING_RECOUNT_COPY.findingsOne
      : fillCount(OLLIE_OPENING_RECOUNT_COPY.findingsMany, remaining)
  const asOf = opening.asOfLabel ? `, as of ${opening.asOfLabel}.` : '.'
  const top =
    shownNeeds && remaining > shownNeeds
      ? ` ${OLLIE_OPENING_RECOUNT_COPY.topShown.replace('{n}', shownNeeds)}`
      : ''
  return `${lead}${count}${asOf}${top}`
}

export const OLLIE_FINDING_ACTION_MODES = Object.freeze({
  NAVIGATE: 'navigate',
  THREAD: 'thread',
})

export function resolveOllieFindingTargetPath(venueId, target, params = null) {
  if (!venueId || !target) return null
  const builder = OLLIE_FINDING_TARGET_ROUTES[target]
  return builder ? builder(venueId, params || {}) : null
}

export function listOllieFindingTargets() {
  return Object.keys(OLLIE_FINDING_TARGET_ROUTES)
}

/**
 * The card's one button: the finding's first action. Open actions with a
 * known screen navigate; everything else opens the thread (nothing is saved
 * from the card).
 */
export function pickOllieFindingCardAction(finding, venueId) {
  const action = finding?.actions?.[0]
  if (!action) return null
  const path =
    action.kind === 'open'
      ? resolveOllieFindingTargetPath(venueId, action.target, action.params)
      : null
  return {
    id: action.id,
    label: action.label,
    kind: action.kind,
    target: action.target,
    ...(action.params ? { params: action.params } : {}),
    mode: path
      ? OLLIE_FINDING_ACTION_MODES.NAVIGATE
      : OLLIE_FINDING_ACTION_MODES.THREAD,
    path,
  }
}

export function formatOllieMinutes(minutes) {
  if (!Number.isInteger(minutes) || minutes <= 0) return null
  return `About ${minutes} min`
}

export function buildOllieFindingCard(finding, { venueId, asOfLabel } = {}) {
  if (!finding?.checkId || !finding?.copy?.title) return null
  const isNotice = finding.kind === 'notice'
  return {
    checkId: finding.checkId,
    kind: finding.kind,
    isNotice,
    title: finding.copy.title,
    noticed: finding.copy.noticed || null,
    minutesLabel: isNotice
      ? null
      : formatOllieMinutes(finding.estimatedMinutes),
    asOfLabel: asOfLabel ? `as of ${asOfLabel}` : null,
    action: pickOllieFindingCardAction(finding, venueId),
    canSnooze: !isNotice && Boolean(finding.fingerprint),
    /** Good news (a win, a streak, an unlock) keeps its one button only. */
    canAskWhy: !finding.goodNews,
    finding,
  }
}

/**
 * Drop a put-away finding's own question straight away; Express re-reads the
 * rest of the prompts once the snooze is saved.
 */
export function filterOllieStarterPrompts(
  prompts = [],
  { opening = null, hiddenCheckIds = [] } = {},
) {
  if (!hiddenCheckIds.length) return prompts
  const hidden = new Set(hiddenCheckIds)
  const questions = new Set(
    (opening?.findings || [])
      .filter(finding => hidden.has(finding?.checkId))
      .map(finding => finding.askOllie?.question)
      .filter(Boolean),
  )
  return questions.size
    ? prompts.filter(prompt => !questions.has(prompt))
    : prompts
}

/**
 * At the message limit, Why? / How? on a finding that is still showing stay
 * open: Express decides whether that follow-up is free. They close once the
 * finding has gone or today's free follow-ups on it are used up.
 */
export function isOllieChipAskDisabled({
  unavailable = false,
  limitReached = false,
  finding = null,
  followUp = null,
} = {}) {
  if (unavailable) return true
  if (!limitReached) return false
  return finding?.showing === false || followUp?.freeLeftToday === 0
}

/**
 * Suggested questions belong to a fresh general chat only, and never at the
 * message limit (they couldn't be sent).
 */
export function shouldShowOllieStarterPrompts({
  isFindingThread = false,
  messages = [],
  prompts = [],
  isSending = false,
  limitReached = false,
} = {}) {
  return (
    !isFindingThread &&
    !limitReached &&
    !messages.some(item => item?.role === 'user') &&
    prompts.length > 0 &&
    !isSending
  )
}

/**
 * After a successful first free-form reply, offer one more practical step
 * (Activation 2.2) — not a blank end to the first conversation.
 */
export function shouldShowOllieFirstConversationNextSteps({
  isFindingThread = false,
  messages = [],
  nextSteps = null,
  isSending = false,
  limitReached = false,
} = {}) {
  if (isFindingThread || limitReached || isSending) return false
  const prompts = nextSteps?.prompts || []
  if (!prompts.length) return false
  const users = messages.filter(item => item?.role === 'user' && !item?.pending)
  const assistants = messages.filter(
    item => item?.role === 'assistant' && !item?.timedOut,
  )
  return users.length === 1 && assistants.length >= 1
}

/**
 * Opening message + cards. The static intro stands in only when the opening
 * failed or Express fell back to it; while loading we say we're checking
 * rather than flash the intro and then swap it.
 */
/**
 * Today's job button, or — when this plan can't do the job here (Free stock
 * takes on desktop) — Express's honest note in its place, never a button
 * that opens a paywall.
 */
function openingTodayJob(opening, path) {
  if (opening.kind !== 'today_job') return null
  const job = opening.todayJob
  const minutesLabel = formatOllieMinutes(job?.estimatedMinutes)
  if (path && job?.cta?.label) {
    return { label: job.cta.label, path, minutesLabel }
  }
  if (job?.honestNote) {
    return { label: null, path: null, note: job.honestNote, minutesLabel }
  }
  return null
}

export function buildOllieOpeningDisplay({
  opening = null,
  status = 'idle',
  introMessage = '',
  venueId = null,
  hiddenCheckIds = [],
  snoozedRows = {},
} = {}) {
  const loading = status === 'loading' && !opening
  if (loading) {
    return {
      mode: 'loading',
      message: OLLIE_FINDING_CARD_COPY.checking,
      lines: [],
      winLine: null,
      staleNote: null,
      cards: [],
      todayJob: null,
    }
  }

  if (!opening || opening.kind === 'intro' || !opening.message) {
    return {
      mode: 'intro',
      message: introMessage || opening?.message || '',
      lines: [],
      winLine: null,
      staleNote: null,
      cards: [],
      todayJob: null,
    }
  }

  const hidden = new Set(hiddenCheckIds)
  const findings = opening.findings || []
  // A snoozed card keeps its place as "Hidden until … · Undo"; it no longer
  // needs them, so it counts as hidden.
  const cards = findings
    .map(finding => {
      if (!hidden.has(finding?.checkId)) {
        return buildOllieFindingCard(finding, {
          venueId,
          asOfLabel: opening.asOfLabel,
        })
      }
      const row = snoozedRows[finding?.checkId]
      return row?.hiddenUntilLabel
        ? {
            checkId: finding.checkId,
            snoozed: true,
            title: finding.copy?.title || '',
            hiddenUntilLabel: row.hiddenUntilLabel,
            undoFailed: Boolean(row.undoFailed),
            finding,
          }
        : null
    })
    .filter(Boolean)
  const shownCards = cards.filter(card => !card.snoozed)

  const isFindings = opening.kind === 'findings'
  const hiddenNeeds = findings.filter(
    finding => finding?.kind !== 'notice' && hidden.has(finding?.checkId),
  ).length
  const message =
    isFindings && hiddenNeeds
      ? recountOllieOpeningMessage(opening, {
          hiddenNeeds,
          shownNeeds: shownCards.filter(card => !card.isNotice).length,
        })
      : opening.message

  // Findings lines repeat each card's title + noticed, so the cards carry
  // them; once they're put away, their lines go too.
  const lines =
    isFindings && (shownCards.length || hiddenNeeds) ? [] : opening.lines

  const cta = opening.todayJob?.cta
  const todayJobPath =
    opening.kind === 'today_job' && cta?.action
      ? resolveGrowthEngineCtaPath(venueId, cta.action)
      : null

  return {
    mode: 'opening',
    kind: opening.kind,
    message,
    lines: lines || [],
    winLine: opening.winLine || null,
    staleNote: opening.stale ? OLLIE_FINDING_CARD_COPY.stale : null,
    cards,
    todayJob: openingTodayJob(opening, todayJobPath),
  }
}
